import { writable } from 'svelte/store';

// Sidebar visibility — open by default on desktop (>768px), closed on mobile
const sidebarStartOpen = typeof window !== 'undefined' && window.innerWidth > 768;
export const sidebarOpen = writable(sidebarStartOpen);

// Table of contents visibility (for mobile)
export const tocOpen = writable(false);

// Bookmarks panel visibility — open by default only on desktop (≥1200px),
// where it sits inline; on mobile/tablet it becomes a full overlay so start closed
const bookmarksStartOpen = typeof window !== 'undefined' && window.innerWidth >= 1200;
export const bookmarksOpen = writable(bookmarksStartOpen);

// Bookmarks data
export interface Bookmark {
  path: string;
  title: string;
  pinned: boolean;
  timestamp: number;
}

function createBookmarksStore() {
  const STORAGE_KEY = `${__STORAGE_KEY_PREFIX__}-bookmarks`;
  const MAX_RECENT = 10;

  // Load from localStorage
  const stored = typeof localStorage !== 'undefined'
    ? localStorage.getItem(STORAGE_KEY)
    : null;

  const initial: Bookmark[] = stored ? JSON.parse(stored) : [];

  const { subscribe, set, update } = writable<Bookmark[]>(initial);

  // Save to localStorage whenever bookmarks change
  subscribe((bookmarks) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
    }
  });

  return {
    subscribe,
    addRecent(path: string, title: string) {
      update((bookmarks) => {
        const existing = bookmarks.find((b) => b.path === path);

        if (existing) {
          // Already in list — update title/timestamp in place, keep position and pinned status
          return bookmarks.map((b) =>
            b.path === path ? { ...b, title, timestamp: Date.now() } : b
          );
        }

        // New page — append to end
        const newEntry: Bookmark = {
          path,
          title,
          pinned: false,
          timestamp: Date.now(),
        };

        const result = [...bookmarks, newEntry];

        // Trim oldest recent (non-pinned) if over limit
        const recentCount = result.filter((b) => !b.pinned).length;
        if (recentCount > MAX_RECENT) {
          const oldestIndex = result.findIndex((b) => !b.pinned);
          if (oldestIndex !== -1) {
            result.splice(oldestIndex, 1);
          }
        }

        return result;
      });
    },
    pin(path: string) {
      update((bookmarks) =>
        bookmarks.map((b) =>
          b.path === path ? { ...b, pinned: true } : b
        )
      );
    },
    unpin(path: string) {
      update((bookmarks) =>
        bookmarks.map((b) =>
          b.path === path ? { ...b, pinned: false } : b
        )
      );
    },
    remove(path: string) {
      update((bookmarks) => bookmarks.filter((b) => b.path !== path));
    },
    pinSection(path: string, title: string) {
      update((bookmarks) => {
        const existing = bookmarks.find((b) => b.path === path);
        if (existing) {
          // Already exists — ensure it's pinned
          return bookmarks.map((b) =>
            b.path === path ? { ...b, title, pinned: true, timestamp: Date.now() } : b
          );
        }
        // New pinned bookmark
        return [...bookmarks, { path, title, pinned: true, timestamp: Date.now() }];
      });
    },
    clear() {
      set([]);
    },
  };
}

export const bookmarks = createBookmarksStore();

// Menu config
export interface MenuItem {
  label: string;
  path?: string;
  children?: MenuItem[];
}

export const menuConfig = writable<MenuItem[]>([]);

// Load menu config
export async function loadMenuConfig(): Promise<void> {
  try {
    const response = await fetch('/menu.json');
    if (!response.ok) throw new Error('Failed to load menu');
    const data = await response.json();
    menuConfig.set(data.items || []);
  } catch (error) {
    console.error('Error loading menu config:', error);
    // Fallback menu
    menuConfig.set([
      { label: 'Home', path: '/' },
      { label: 'Wiki', path: '/wiki' },
      { label: 'Search', path: '/search' },
    ]);
  }
}
